
import React, { useRef } from 'react';
import { LayoutDashboard, Users, Trophy, ClipboardList, Calendar, Camera, LogOut, Building2, ChevronRight, BarChart2, Settings as SettingsIcon, ShieldCheck, ChevronDown } from 'lucide-react';
import { ViewState, UserProfile, Organization, Team, STAFF_ROLES, ROLE_LABELS } from '../types';

interface SidebarProps {
  currentView: ViewState;
  setView: (view: ViewState) => void;
  isMobileMenuOpen: boolean;
  setIsMobileMenuOpen: (isOpen: boolean) => void;
  schoolLogo: string | null;
  setSchoolLogo: (logo: string | null) => void;
  user: UserProfile | null;
  currentOrg: Organization | null;
  onSwitchOrg: () => void;
  onSignOut: () => void;
  teams?: Team[];
  activeTeamId?: string;
  setActiveTeamId?: (id: string) => void;
}

const Sidebar: React.FC<SidebarProps> = ({
  currentView,
  setView,
  isMobileMenuOpen,
  setIsMobileMenuOpen,
  schoolLogo,
  setSchoolLogo,
  user,
  currentOrg,
  onSwitchOrg,
  onSignOut,
  teams,
  activeTeamId,
  setActiveTeamId,
}) => {
  const role = currentOrg?.role || '';
  const isCoach = STAFF_ROLES.includes(role) || !!currentOrg?.isAdmin;
  const isAdmin = !!currentOrg?.isAdmin;

  const menuItems = [
    { id: ViewState.DASHBOARD, label: 'Dashboard', icon: LayoutDashboard, restricted: true },
    { id: ViewState.TEAM, label: 'Team Mgmt', icon: Users, restricted: true },
    { id: ViewState.GAME, label: 'Game Time', icon: Trophy, restricted: false },
    { id: ViewState.PRACTICE, label: 'Practice & Drills', icon: ClipboardList, restricted: true },
    { id: ViewState.SCHEDULE, label: 'Schedule', icon: Calendar, restricted: false },
    { id: ViewState.STATS, label: 'Stats', icon: BarChart2, restricted: false },
  ];

  // Filter items based on role
  const visibleItems = menuItems.filter(item => {
    if (!isCoach && item.restricted) return false;
    return true;
  });

  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleNav = (id: ViewState) => {
    setView(id);
    setIsMobileMenuOpen(false);
  };

  const handleLogoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setSchoolLogo(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  return (
    <>
      {/* Mobile Overlay */}
      <div 
        className={`fixed inset-0 bg-black/50 z-40 transition-opacity lg:hidden ${isMobileMenuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}
        onClick={() => setIsMobileMenuOpen(false)}
      />

      {/* Sidebar Container */}
      <aside className={`
        fixed lg:static inset-y-0 left-0 z-50 w-64 bg-slate-900 text-white transform transition-transform duration-200 ease-in-out
        ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        flex flex-col
      `}>
        {/* App Logo */}
        <div className="px-4 py-3 border-b border-slate-700">
          <div className="text-white font-bold text-lg leading-tight">Sideout<span className="text-orange-500">IQ</span></div>
          <div className="text-white/60 text-[10px] font-semibold tracking-widest uppercase">Command the Court</div>
        </div>

        {/* Org Header */}
        <div className="p-4 border-b border-slate-700">
           {currentOrg && (
            <div className="flex items-center justify-between group">
                <div 
                   className={`flex items-center gap-3 overflow-hidden ${isCoach ? 'cursor-pointer' : ''}`}
                   onClick={() => isCoach ? setView(ViewState.ORGANIZATION) : null}
                   title={isCoach ? "Manage Organization" : ""}
                >
                    <div className="w-10 h-10 rounded-lg bg-blue-600 flex items-center justify-center shrink-0">
                        <Building2 className="w-6 h-6 text-white" />
                    </div>
                    <div className="min-w-0">
                        <h1 className="text-sm font-bold truncate">{currentOrg.name}</h1>
                        <p className="text-xs text-slate-400 truncate">Code: {currentOrg.joinCode}</p>
                    </div>
                </div>
                
                {/* Context Menu / Switcher */}
                <button 
                   onClick={onSwitchOrg}
                   className="p-1 hover:bg-slate-800 rounded text-slate-500 hover:text-white transition-colors"
                   title="Switch Organization"
                >
                   <ChevronRight className="w-4 h-4" />
                </button>
            </div>
           )}
        </div>

        {/* Global team selector */}
        {teams && teams.length > 1 && (
          <div className="px-4 py-3 border-b border-slate-700">
            <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Active Team</p>
            <div className="relative">
              <select
                value={activeTeamId || ''}
                onChange={e => setActiveTeamId?.(e.target.value)}
                className="w-full appearance-none bg-slate-800 border border-slate-600 text-white text-sm font-medium pl-3 pr-8 py-2 rounded-lg outline-none cursor-pointer hover:bg-slate-700 transition-colors"
              >
                {teams.map(t => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>
              <ChevronDown className="w-4 h-4 text-slate-400 absolute right-2 top-2.5 pointer-events-none" />
            </div>
          </div>
        )}

        {/* User Info */}
        {user && (
            <div
              className="px-4 py-3 bg-slate-800/50 flex items-center gap-3 cursor-pointer hover:bg-slate-700/50 transition-colors"
              onClick={() => handleNav(ViewState.ORGANIZATION)}
              title="Settings & Profile"
            >
                <div className="w-8 h-8 rounded-full bg-slate-700 flex items-center justify-center text-xs font-bold text-slate-300 shrink-0">
                    {user.fullName.charAt(0)}
                </div>
                <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium truncate">{user.fullName}</div>
                    <div className="text-xs text-slate-500 truncate capitalize flex items-center gap-1">
                        {isAdmin && <ShieldCheck className="w-3 h-3 text-blue-400 shrink-0" />}
                        {ROLE_LABELS[currentOrg?.role || ''] || 'Member'}
                    </div>
                </div>
            </div>
        )}

        <nav className="flex-1 p-4 space-y-2 overflow-y-auto">
          {visibleItems.map((item) => (
            <button
              key={item.id}
              onClick={() => handleNav(item.id)}
              className={`
                w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors
                ${currentView === item.id 
                  ? 'bg-blue-600 text-white shadow-lg shadow-blue-900/50' 
                  : 'text-slate-400 hover:bg-slate-800 hover:text-white'}
              `}
            >
              <item.icon className="w-5 h-5" />
              <span className="font-medium">{item.label}</span>
            </button>
          ))}

          <button
             onClick={() => handleNav(ViewState.ORGANIZATION)}
             className={`
               w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors mt-4 border-t border-slate-800 pt-4
               ${(currentView === ViewState.ORGANIZATION || currentView === ViewState.SETTINGS)
                 ? 'bg-blue-600 text-white shadow-lg shadow-blue-900/50'
                 : 'text-slate-400 hover:bg-slate-800 hover:text-white'}
             `}
          >
             <SettingsIcon className="w-5 h-5" />
             <span className="font-medium">Settings</span>
          </button>
        </nav>

        <div className="p-4 border-t border-slate-800">
          <button 
            onClick={onSignOut}
            className="w-full flex items-center space-x-3 px-4 py-3 rounded-lg text-slate-400 hover:bg-red-900/20 hover:text-red-400 transition-colors"
          >
            <LogOut className="w-5 h-5" />
            <span className="font-medium">Sign Out</span>
          </button>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
